// ═══════════════════════════════════════════════════════════════════════════
// Plan tiers, allocations, and feature gates.
//
// Tiers: free → creator → studio → network
//
// Mini-spec #5 reshaped this:
//   • Renamed `pro` → `creator` everywhere
//   • Free uses dailyCredits (3/day, refilled at midnight UTC) instead of
//     a monthly allotment
//   • Each tier now declares the features the conversion modals + Tier-
//     Gate components key off (variants, trend intelligence, sponsor
//     matching, team seats, deep dive, daily bonus, queue priority)
//
// `subscription_tiers` rows in Supabase are the runtime source of truth
// (migration 026) — this file is the in-app cache so we don't round-trip
// the DB to render upgrade UI.
//
// Legacy aliases (`pro`, `premier`, `enterprise`) resolve to their modern
// equivalents so call sites that haven't been updated yet keep working.
// ═══════════════════════════════════════════════════════════════════════════

export const TIER_LIMITS = {
  free: {
    label:              'Free',
    monthlyCredits:     0,       // not used for free
    dailyCredits:       3,       // refills every 24h at midnight UTC
    refillInterval:     'daily',
    maxShows:           1,
    variants:           1,
    trendIntelligence:  false,
    sponsorMatching:    false,
    teamSeats:          0,
    deepDive:           false,
    dailyBonus:         0,
    queuePriority:      'standard',
    matches:            3,
    rolloverMultiplier: 0.0,
    priceMonthly:       0,
    priceAnnual:        0,
  },
  // Guest+ — the cheap unlock for GUEST accounts. Free guests get capped
  // matches + no pitching (same as 'free'); $9/mo lifts the cap and enables
  // pitching. Guests don't run shows, so no episode/credit escalation.
  guest_plus: {
    label:              'Guest+',
    monthlyCredits:     0,
    dailyCredits:       3,
    refillInterval:     'daily',
    maxShows:           0,
    variants:           1,
    trendIntelligence:  false,
    sponsorMatching:    false,
    teamSeats:          0,
    deepDive:           false,
    dailyBonus:         0,
    queuePriority:      'standard',
    matches:            Infinity,
    rolloverMultiplier: 0.0,
    priceMonthly:       9,
    priceAnnual:        90,
  },
  creator: {
    label:              'Creator',
    monthlyCredits:     50,
    dailyCredits:       0,       // not used for paid tiers
    refillInterval:     'monthly',
    maxShows:           3,
    variants:           2,
    trendIntelligence:  true,
    sponsorMatching:    false,
    teamSeats:          0,
    deepDive:           false,
    dailyBonus:         2,       // when subscription credits hit 0
    queuePriority:      'priority',
    matches:            Infinity,
    rolloverMultiplier: 1.0,
    priceMonthly:       29,
    priceAnnual:        249,     // = $20.75/mo annual
  },
  studio: {
    label:              'Studio',
    monthlyCredits:     150,
    dailyCredits:       0,
    refillInterval:     'monthly',
    maxShows:           10,
    variants:           2,
    trendIntelligence:  true,
    sponsorMatching:    true,
    teamSeats:          3,
    deepDive:           true,
    dailyBonus:         2,
    queuePriority:      'priority',
    matches:            Infinity,
    rolloverMultiplier: 1.0,
    priceMonthly:       79,
    priceAnnual:        708,     // = $59/mo annual
  },
  network: {
    label:              'Network',
    monthlyCredits:     500,
    dailyCredits:       0,
    refillInterval:     'monthly',
    maxShows:           9999,
    variants:           2,
    trendIntelligence:  true,
    sponsorMatching:    true,
    teamSeats:          999,
    deepDive:           true,
    dailyBonus:         5,
    queuePriority:      'highest',
    matches:            Infinity,
    rolloverMultiplier: 1.0,
    priceMonthly:       null,    // custom invoicing
    priceAnnual:        null,
  },
};

// Legacy aliases — resolve to their modern equivalents. Allows old user
// rows or call sites that still reference `pro` / `premier` / `enterprise`
// to keep working through the migration window.
const LEGACY_MAP = {
  pro:        'creator',
  premier:    'studio',
  enterprise: 'network',
};

const TIER_ORDER = ['free', 'creator', 'studio', 'network'];

function normalize(plan) {
  const raw = (plan || 'free').toString().toLowerCase();
  const canonical = LEGACY_MAP[raw] || raw;
  return TIER_LIMITS[canonical] ? canonical : 'free';
}

// Backward-compat: PLAN_LIMITS is the old shape some code still imports.
// Keep it pointed at the new TIER_LIMITS map with a `credits` property
// matching whichever bucket is the relevant cap.
export const PLAN_LIMITS = Object.fromEntries(
  Object.entries(TIER_LIMITS).map(([id, t]) => [
    id,
    {
      shows:   t.maxShows,
      // For free, the cap is daily; for paid, monthly. Some callers want
      // the rough monthly equivalent — daily * 30 is a reasonable proxy.
      credits: t.refillInterval === 'daily' ? t.dailyCredits * 30 : t.monthlyCredits,
      matches: t.matches,
      label:   t.label,
    },
  ])
);
// And legacy aliases for the old import shape.
PLAN_LIMITS.pro        = PLAN_LIMITS.creator;
PLAN_LIMITS.premier    = PLAN_LIMITS.studio;
PLAN_LIMITS.enterprise = PLAN_LIMITS.network;

// Top-level credit costs — kept for legacy callers. New code should import
// from creditCosts.js.
export const CREDITS_PER_EPISODE       = 6;
export const CREDITS_PER_SECTION_REGEN = 2;
export const CREDIT_CYCLE_DAYS         = 30;

// ── Super-admin / founder god-mode ───────────────────────────────────────────
// Full admins (role='admin' or the founder email allowlist — Drew + Stephanie)
// and any unlimited profile get treated as the top tier everywhere gates are
// checked, so no match cap, show limit, or feature flag ever applies to them.
// Same spirit as the unlimited_credits bypass in summarizeCredits: resolve it
// once, at the plan level, instead of sprinkling `if (isAdmin)` at call sites.

/** True when this profile should bypass every plan gate. `isAdmin` is the
 *  canonical flag from AuthContext (role or email allowlist); the profile
 *  columns are a fallback so prop-only components still resolve correctly. */
export function isUnlimitedProfile(profile, isAdmin = false) {
  return !!(
    isAdmin ||
    profile?.is_admin === true ||
    profile?.unlimited_credits === true
  );
}

/** The plan tier to gate a profile by — 'network' (top tier) for god-mode
 *  users, otherwise their real plan. Pass this to summarizeMatchAccess /
 *  summarizeShowUsage / hasFeature instead of raw profile.plan. */
export function effectivePlan(profile, isAdmin = false) {
  return isUnlimitedProfile(profile, isAdmin) ? 'network' : (profile?.plan || 'free');
}

// ── Public API ─────────────────────────────────────────────────────────────

export function getTier(plan)       { return TIER_LIMITS[normalize(plan)];          }
export function getShowLimit(plan)  { return TIER_LIMITS[normalize(plan)].maxShows; }
export function getMatchLimit(plan) { return TIER_LIMITS[normalize(plan)].matches;  }
export function getCreditLimit(plan) {
  const t = TIER_LIMITS[normalize(plan)];
  return t.refillInterval === 'daily' ? t.dailyCredits : t.monthlyCredits;
}
export function getPlanLabel(plan)  { return TIER_LIMITS[normalize(plan)].label;    }

/** Does this tier have access to feature `name`? */
export function hasFeature(plan, name) {
  const t = TIER_LIMITS[normalize(plan)];
  if (!t) return false;
  return !!t[name];
}

export function getNextTier(plan) {
  const tier = normalize(plan);
  const idx = TIER_ORDER.indexOf(tier);
  if (idx < 0 || idx >= TIER_ORDER.length - 1) return null;
  const nextKey = TIER_ORDER[idx + 1];
  return { key: nextKey, label: TIER_LIMITS[nextKey].label, shows: TIER_LIMITS[nextKey].maxShows };
}

/** Account-type-aware upgrade target.
 *
 *  ⚠️ Guest+ is defined above but is NOT PURCHASABLE yet: it has no row in
 *  `subscription_tiers` and no Stripe price, so /api/stripe-checkout returns
 *  "Unknown tier: guest_plus" and it never renders on /pricing (that page
 *  lists tiers from the DB). Advertising it sent guests to a checkout that
 *  can't complete, so this returns the normal ladder for now.
 *
 *  To switch it on: add the guest_plus row to subscription_tiers, add it to
 *  TIER_ORDER, run `npm run setup:stripe`, then restore the guest branch:
 *      if (plan === 'free' && profile?.account_type === 'guest') {
 *        const g = TIER_LIMITS.guest_plus;
 *        return { key: 'guest_plus', label: g.label, shows: g.maxShows, priceMonthly: g.priceMonthly };
 *      }
 */
export function nextUpgradeTier(profile) {
  return getNextTier(profile?.plan);
}

/**
 * Free tier sees only the top few matches. Everything else is full access.
 */
export function summarizeMatchAccess(plan, totalFound = 0) {
  const p = normalize(plan);
  const allowed = getMatchLimit(p);
  const unlimited = allowed === Infinity;
  const visible = unlimited ? totalFound : Math.min(totalFound, allowed);
  return {
    plan: p,
    planLabel: getPlanLabel(plan),
    unlimited,
    allowed,
    totalFound,
    visible,
    hiddenCount: unlimited ? 0 : Math.max(0, totalFound - visible),
    canPitch: unlimited,
    nextTier: getNextTier(plan),
  };
}

/**
 * Show usage against the plan's show limit.
 *   status: 'ok' | 'warning' (≥80%) | 'limit'
 */
export function summarizeShowUsage(plan, showsUsed = 0) {
  const allowed = getShowLimit(plan);
  const unlimited = allowed >= 9999;
  const pct = unlimited ? 0 : showsUsed / allowed;
  const canCreate = unlimited || showsUsed < allowed;
  let status = 'ok';
  if (!unlimited) {
    if (showsUsed >= allowed) status = 'limit';
    else if (pct >= 0.8) status = 'warning';
  }
  return {
    plan: normalize(plan),
    planLabel: getPlanLabel(plan),
    showsUsed,
    showsAllowed: allowed,
    unlimited,
    canCreate,
    status,
    nextTier: getNextTier(plan),
  };
}

/**
 * Summarize a user's credit position for display.
 *
 * Accepts the new `user_credits` row shape from migration 018+026:
 *   { balance, topup_balance, monthly_allotment,
 *     last_refill_at, last_subscription_reset_date,
 *     daily_bonus_last_refill }
 *
 * Output shape stays stable so callers don't have to think about whether
 * a tier uses daily or monthly refill — the `creditsAllowed`,
 * `creditsRemaining`, `nextRefillAt` fields are what they read.
 */
export function summarizeCredits(plan, creditsRowOrProfile) {
  const p = normalize(plan);
  const tier = TIER_LIMITS[p];
  const isDaily = tier.refillInterval === 'daily';

  const row = creditsRowOrProfile || {};

  // Mini-spec #65 — unlimited bypass at the source. When the row
  // (which can be either a credits row OR a profile object — callers
  // pass either) carries unlimited_credits = true, every gate
  // returns success immediately. This is what every other component
  // in the app reads through, so the gate the build flow consults
  // (handleBuildEpisode → summarizeCredits) finally respects the
  // flag without needing per-call-site bypasses.
  if (row.unlimited_credits === true) {
    return {
      plan: p,
      planLabel: tier.label,
      creditsUsed: 0,
      creditsAllowed: Infinity,
      creditsRemaining: Infinity,
      creditsCap: Infinity,
      refillInterval: tier.refillInterval,
      unlimited: true,
      canBuild: true,
      status: 'ok',
      cycleOver: false,
      nextRefillAt: null,
      renewsAt: null,
      nextTier: null,
    };
  }

  const balance       = Math.max(0, row.balance ?? 0);
  const topup         = Math.max(0, row.topup_balance ?? 0);
  const total         = balance + topup;

  const allowed = isDaily ? tier.dailyCredits : tier.monthlyCredits;
  const cap     = isDaily
    ? tier.dailyCredits
    : Math.round(tier.monthlyCredits * (tier.rolloverMultiplier || 1.0));

  const remaining = total;
  const used      = isDaily
    ? Math.max(0, allowed - balance)
    : Math.max(0, (row.monthly_allotment ?? allowed) - balance);

  const canBuild = remaining > 0;
  const pct      = allowed === 0 ? 0 : used / allowed;
  let status = 'ok';
  if (allowed > 0) {
    if (remaining <= 0) status = 'limit';
    else if (pct >= 0.8) status = 'warning';
  }

  // Next refill timestamp for the countdown UI.
  let nextRefillAt = null;
  if (isDaily) {
    // Free tier refills at midnight UTC.
    const now = new Date();
    const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0));
    nextRefillAt = next.toISOString();
  } else if (row.last_refill_at) {
    const ms = new Date(row.last_refill_at).getTime() + CREDIT_CYCLE_DAYS * 86_400_000;
    nextRefillAt = new Date(ms).toISOString();
  }

  return {
    plan: p,
    planLabel: tier.label,
    creditsUsed: used,
    creditsAllowed: allowed,
    creditsRemaining: remaining,
    creditsCap: cap,
    refillInterval: tier.refillInterval,
    unlimited: false,
    canBuild,
    status,
    cycleOver: false,
    nextRefillAt,
    renewsAt: nextRefillAt, // alias for backward compat
    nextTier: getNextTier(plan),
  };
}
